// Nouveau dossier : site existant ou nouveau (+ client), titre, demande du client, dépôt des pièces, puis suivi du traitement.
import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ErreurApi, messageErreur } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { Alerte, Bouton, Champ, ChampChoix, ChampTexte, EntetePage, EtatErreur, Panneau, useNotifier } from '../../components/ui.tsx';
import { DepotPieces, envoyerPieces, type PieceADeposer } from './commun.tsx';

export function PageNouveauDossier() {
  const { peut } = useSession();
  const nav = useNavigate();
  const qc = useQueryClient();
  const notifier = useNotifier();
  const [params] = useSearchParams();
  const sites = useQuery({ queryKey: ['sites'], queryFn: () => api.get<{ id: string; name: string; client_name: string | null; demo: boolean }[]>('/api/sites') });
  const clients = useQuery({ queryKey: ['clients'], queryFn: () => api.get<{ id: string; name: string }[]>('/api/clients') });
  const [mode, setMode] = useState<'existant' | 'nouveau'>(params.get('site') ? 'existant' : 'existant');
  const [f, setF] = useState({ siteId: params.get('site') ?? '', siteNom: '', client: '', title: '', clientRequest: '' });
  const [pieces, setPieces] = useState<PieceADeposer[]>([]);
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState<string | null>(null);
  const maj = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  if (!peut('dossier.ecrire')) return <EtatErreur erreur={{ code: 'droit_insuffisant', message: 'Vos droits ne permettent pas de créer un dossier d’étude.' }} />;

  const soumettre = async (e: FormEvent) => {
    e.preventDefault();
    const err: Record<string, string> = {};
    if (mode === 'existant' && !f.siteId) err.siteId = 'Choisissez le site concerné.';
    if (mode === 'nouveau' && !f.siteNom.trim()) err.siteNom = 'Indiquez le nom du nouveau site.';
    if (!f.title.trim()) err.title = 'Donnez un titre au dossier.';
    setErreurs(err); setErreur(null);
    if (Object.keys(err).length) return;
    setEnvoi('Création du dossier…');
    try {
      const x = await api.post<{ id: string }>('/api/dossiers', {
        ...(mode === 'existant' ? { siteId: f.siteId } : { nouveauSite: { name: f.siteNom, clientName: f.client || null } }),
        title: f.title, clientRequest: f.clientRequest || null,
      });
      if (pieces.length) {
        setEnvoi(`Envoi de ${pieces.length} pièce${pieces.length > 1 ? 's' : ''}…`);
        const r = await envoyerPieces(x.id, pieces);
        const echecs = r.filter((p) => p.erreur);
        if (echecs.length) notifier(`${echecs.length} pièce(s) non importée(s) : ${echecs.map((p) => `${p.nom} (${p.erreur})`).join(' ; ')}`, true);
      }
      await qc.invalidateQueries({ queryKey: ['dossiers'] });
      await qc.invalidateQueries({ queryKey: ['sites'] });
      notifier('Dossier créé. Les pièces sont lues puis analysées automatiquement.');
      nav(`/etudes/${x.id}`);
    } catch (er) {
      if (er instanceof ErreurApi && Array.isArray(er.details)) setErreurs(Object.fromEntries((er.details as any[]).map((d) => [d.champ, d.message])));
      setErreur(messageErreur(er));
    } finally { setEnvoi(null); }
  };

  return (
    <div className="pile-l" style={{ maxWidth: 900 }}>
      <EntetePage titre="Nouveau dossier d’étude" sousTitre="Le dossier rassemble les pièces reçues ; leur analyse fait ressortir les exigences, les écarts et les questions à poser avant le devis." />
      <form onSubmit={soumettre} noValidate className="pile-l">
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <Panneau titre="Site">
          <div className="pile">
            <div className="segment et-segment" role="group" aria-label="Site du dossier">
              <button type="button" aria-pressed={mode === 'existant'} onClick={() => setMode('existant')}>Site existant</button>
              <button type="button" aria-pressed={mode === 'nouveau'} onClick={() => setMode('nouveau')} disabled={!peut('site.ecrire')}>Nouveau site</button>
            </div>
            {mode === 'existant' ? (
              <ChampChoix libelle="Site concerné" value={f.siteId} onChange={maj('siteId')} erreur={erreurs.siteId} vide={sites.isLoading ? 'Chargement…' : 'Choisir un site'}
                options={(sites.data ?? []).map((s) => ({ valeur: s.id, libelle: `${s.name}${s.client_name ? ` — ${s.client_name}` : ''}${s.demo ? ' (démo)' : ''}` }))}
                aide={sites.data?.length === 0 ? 'Aucun site accessible : créez un nouveau site.' : undefined} />
            ) : (
              <div className="grille-form">
                <Champ libelle="Nom du nouveau site" value={f.siteNom} onChange={maj('siteNom')} erreur={erreurs.siteNom || erreurs['nouveauSite.name']} placeholder="ex. Résidence Les Tilleuls" />
                <Champ libelle="Client" list="et-clients" value={f.client} onChange={maj('client')} aide="Client existant ou nouveau nom." />
                <datalist id="et-clients">{clients.data?.map((c) => <option key={c.id} value={c.name} />)}</datalist>
              </div>
            )}
          </div>
        </Panneau>
        <Panneau titre="Dossier">
          <div className="pile">
            <Champ libelle="Titre du dossier" value={f.title} onChange={maj('title')} erreur={erreurs.title} placeholder="ex. Remplacement des tableaux électriques" />
            <ChampTexte libelle="Demande du client" value={f.clientRequest} onChange={maj('clientRequest')} rows={5}
              aide="Recopiez la demande telle que formulée par le client : elle sert de référence « demandé » face au CCTP (« prévu ») et au chiffrage." />
          </div>
        </Panneau>
        <Panneau titre="Pièces">
          <DepotPieces pieces={pieces} changer={setPieces} />
        </Panneau>
        <div className="et-barre-actions">
          <Bouton type="submit" variante="principal" chargement={!!envoi}>{envoi ?? (pieces.length ? 'Créer le dossier et importer les pièces' : 'Créer le dossier')}</Bouton>
          <Bouton onClick={() => nav(-1)} disabled={!!envoi}>Annuler</Bouton>
        </div>
      </form>
    </div>
  );
}
